import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from '../common/Toast/toast';
import PopupDone, { usePopupDone } from "../common/PopupDone/PopupDone.jsx";
import { Zap } from "lucide-react";
import debounce from "lodash/debounce";
import {
  deleteEmailEventConfig,
  getEmailEventConfigs,
  getEmailTemplates,
} from "../../../services/MailerService";
import { useAuth } from "../../../context/AuthContext";
import NewCustomDataTable from "../common/NewCustomDataTable.jsx";
import SettingsFormModal from "../common/SettingsFormModal.jsx";
import AddEmailEventConfig from "./AddEmailEventConfig.jsx";
import useScrollLoadMore from "../common/useScrollLoadMore";
import NewCustomAsyncDropdown from "../common/NewCustomAsyncSelectDropdown.jsx";
// Built to the same plan as /list-attendance, so it reads that page's
// single stylesheet rather than keeping one of its own.
import "../Trainer/TrainerDashboard.css";
import FilterSheet from "../common/FilterSheet/FilterSheet";
import useDebouncedCallback from "../common/useDebouncedCallback";
import { confirm } from '../common/Confirm/confirm';

const DASH = "—";
const TEMPLATE_PAGE_SIZE = 20;

const textOf = (value) => {
  const s = String(value ?? "").trim();
  return s === "" ? DASH : s;
};

const dateOf = (value) => {
  if (!value) return DASH;
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? DASH
    : d.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
};

/* "student-enrolled" reads as "Student Enrolled". */
const eventLabelOf = (key) =>
  String(key || "")
    .split(/[-_]/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

/* The template a config fires. Only ever its name -- the field can also
   come back as a bare id, and an id is not an answer. */
const templateOf = (row) => {
  const list = row?.emailtemplate;
  if (Array.isArray(list) && list.length > 0) return list[0] || null;
  return null;
};

const templateNameOf = (row) => {
  const template = templateOf(row);
  return template?.name ? String(template.name).trim() : "";
};

/* ── The record popup ────────────────────────────────────────────────
   Same plan as the attendance list's popup: title and a pgm-ident row of
   labelled facts in the header, pgm-section blocks in the body each led
   by a pgm-section-title, pgm-table for anything with more than one
   value, pgm-empty when there is nothing, and a footer whose actions sit
   left of a filled Close.
   ------------------------------------------------------------------ */
const ConfigDetailModal = ({ detail, onClose, onEdit, onDelete, canEdit, canDelete }) => {
  useEffect(() => {
    if (!detail) return undefined;
    const onKey = (e) => {
      // A form opened from here sits on top; Escape is its, not this popup's.
      if (e.key === "Escape" && !document.querySelector(".pg-modal--over")) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [detail, onClose]);

  // A delete from here swaps the record for the done panel, which closes
  // the popup five seconds later.
  const { done } = usePopupDone(Boolean(detail));

  if (!detail) return null;

  const isActive = !detail.isDeleted;
  const statusLabel = isActive ? "Active" : "Inactive";
  const template = templateOf(detail);
  const templateName = templateNameOf(detail);

  const config = [
    { label: "Event key", value: textOf(detail.eventKey), mono: true },
    { label: "Event label", value: eventLabelOf(detail.eventKey) || DASH },
    { label: "Created", value: dateOf(detail.createdAt) },
    { label: "Last updated", value: dateOf(detail.updatedAt) },
  ];

  /* The template's own record, and only where the join brought it. */
  const templateFacts = template
    ? [
        { label: "Name", value: textOf(template.name) },
        { label: "Slug", value: textOf(template.slug), mono: true },
        { label: "Subject", value: textOf(template.subject) },
      ]
    : [];

  return (
    <div
      className="pg-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="eec-detail-title"
      onClick={onClose}
    >
      <div
        className="pg-modal-panel pg-modal-panel--wide"
        onClick={(e) => e.stopPropagation()}
      >
        {/* The event is what the record is about, so it is the title --
            written as words, with the raw key in the facts below. */}
        <header className="pgm-head">
          <div className="pgm-head-main">
            <h2 className="pgm-batch" id="eec-detail-title">
              {eventLabelOf(detail.eventKey) || "Email event configuration"}
            </h2>
            <dl className="pgm-ident">
              <div className="pgm-ident-row">
                <dt>Event key</dt>
                <dd>{textOf(detail.eventKey)}</dd>
              </div>
              <div className="pgm-ident-row">
                <dt>Template</dt>
                <dd>{templateName || DASH}</dd>
              </div>
              <div className="pgm-ident-row">
                <dt>Status</dt>
                <dd>{statusLabel}</dd>
              </div>
            </dl>
          </div>

          <button
            type="button"
            className="pg-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            &times;
          </button>
        </header>

        <div className="pg-modal-body">
          {done ? (
            <PopupDone message={done} onClose={onClose} />
          ) : (
            <>
              <section className="pgm-section">
                <h3 className="pgm-section-title">Configuration</h3>
                <table className="pgm-table">
                  <thead>
                    <tr>
                      <th className="pgm-num">#</th>
                      <th>Detail</th>
                      <th>Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {config.map((f, i) => (
                      <tr key={f.label}>
                        <td className="pgm-num">{i + 1}</td>
                        <td>{f.label}</td>
                        <td className={f.mono ? "pgm-mono" : undefined}>{f.value}</td>
                      </tr>
                    ))}
                    <tr>
                      <td className="pgm-num">{config.length + 1}</td>
                      <td>Status</td>
                      <td>
                        <span
                          className={`pgm-pill ${
                            isActive ? "pgm-pill--good" : "pgm-pill--bad"
                          }`}
                        >
                          {statusLabel}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </section>

              <section className="pgm-section">
                <h3 className="pgm-section-title">Sends this template</h3>
                {templateFacts.length === 0 ? (
                  <p className="pgm-empty">
                    No email template came back with this configuration.
                  </p>
                ) : (
                  <table className="pgm-table">
                    <thead>
                      <tr>
                        <th className="pgm-num">#</th>
                        <th>Detail</th>
                        <th>Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {templateFacts.map((f, i) => (
                        <tr key={f.label}>
                          <td className="pgm-num">{i + 1}</td>
                          <td>{f.label}</td>
                          <td className={f.mono ? "pgm-mono" : undefined}>{f.value}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </section>
            </>
          )}
        </div>

        {/* What you can do with the record sits with Close, at the end.
            Close stays last and is the only filled button, so the
            destructive one is never the default under the cursor. */}
        {/* The done panel replaces the actions once a delete lands. */}
        {!done && (
          <footer className="pg-modal-foot">
            <div className="pgm-actions">
              {canEdit && (
                <button type="button" className="pgm-btn" onClick={onEdit}>
                  Edit
                </button>
              )}
              {canDelete && (
                <button
                  type="button"
                  className="pgm-btn pgm-btn--danger"
                  onClick={onDelete}
                >
                  Delete
                </button>
              )}
            </div>
            <button type="button" className="pg-btn pg-btn--main" onClick={onClose}>
              Close
            </button>
          </footer>
        )}
      </div>
    </div>
  );
};

const ListEmailEventConfig = () => {
  const [rows, setRows] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  // 25 a batch. The list scrolls rather than paging, so this is how much
  // arrives per fetch, not how much the reader has to click through.
  const [recordsPerPage, setRecordsPerPage] = useState(25);
  const [loading, setLoading] = useState(true);
  // Fetching the next scroll batch. Kept apart from `loading` because that
  // one draws a veil over the whole table -- right for a fresh load, wrong
  // for adding rows to the bottom of a list being read.
  const [loadingMore, setLoadingMore] = useState(false);
  const [detail, setDetail] = useState(null);
  /* The add/edit dialog. null is closed; {} is a new configuration;
     { recordId } edits that one. The form is the same component
     /add-email-event-config renders -- it just reports back here instead
     of navigating. */
  const [formModal, setFormModal] = useState(null);

  // What is typed into the filter section but not yet applied.
  const [searchDraft, setSearchDraft] = useState("");
  const [templateDraft, setTemplateDraft] = useState(null);
  const [eventKeyDraft, setEventKeyDraft] = useState("");

  // What is actually in force.
  const [searchTerm, setSearchTerm] = useState("");
  const [templateId, setTemplateId] = useState("");
  const [eventKey, setEventKey] = useState("");

  /* Event keys are not a list the API publishes, so the only ones that can
     be offered are the ones already loaded. The set grows as the list
     scrolls; the search box covers anything not yet seen. */
  const [eventKeysSeen, setEventKeysSeen] = useState([]);

  // The template picker's own paged option list.
  const [templateOptions, setTemplateOptions] = useState([]);
  const [templatePage, setTemplatePage] = useState(1);
  const [templateHasMore, setTemplateHasMore] = useState(true);
  const [templateLoading, setTemplateLoading] = useState(false);
  const [templateSearch, setTemplateSearch] = useState("");

  const isFetching = useRef(false);
  const appendRef = useRef(false);
  const listRef = useRef(null);
  // The foot under the list. It is what the observer watches, and it
  // sits in the normal flow of the page -- no pane of its own.
  const listEndRef = useRef(null);
  const hasShownToast = useRef(false);

  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  const canView = hasPermission("email-event-config", "view", "lms");
  const canAdd = hasPermission("email-event-config", "add", "lms");
  const canEdit = hasPermission("email-event-config", "update", "lms");
  const canDelete = hasPermission("email-event-config", "delete", "lms");

  useEffect(() => {
    if (!canView) {
      if (!hasShownToast.current) {
        toast.error(
          "You do not have permission to view Email Event Configurations.",
        );
        navigate("/");
        hasShownToast.current = true;
      }
    }
  }, [canView, navigate]);

  // ─── The template picker ──────────────────────────────────────────────

  const loadTemplates = useCallback(async (search, page, append) => {
    setTemplateLoading(true);
    try {
      const response = await getEmailTemplates(
        TEMPLATE_PAGE_SIZE,
        page,
        search || "",
        search ? "name" : "",
      );
      const list = Array.isArray(response?.data) ? response.data : [];
      const options = list.map((t) => ({ value: t.id, label: t.name, template: t }));
      setTemplateOptions((prev) => (append ? [...prev, ...options] : options));
      setTemplateHasMore(list.length === TEMPLATE_PAGE_SIZE);
    } catch (error) {
      console.error("Error fetching email templates:", error);
      if (!append) setTemplateOptions([]);
      setTemplateHasMore(false);
    } finally {
      setTemplateLoading(false);
    }
  }, []);

  const handleTemplateSearch = useMemo(
    () =>
      debounce((value) => {
        setTemplateSearch(value);
        setTemplatePage(1);
        loadTemplates(value, 1, false);
      }, 500),
    [loadTemplates],
  );

  // ─── Fetch ────────────────────────────────────────────────────────────

  const fetchConfigs = useCallback(async () => {
    if (isFetching.current) return;
    isFetching.current = true;
    // Captured before the request: the overlay must never appear for a
    // scroll batch, and reading the live ref after an await could get the
    // wrong answer if anything reset it meanwhile.
    const isAppend = appendRef.current;
    if (isAppend) setLoadingMore(true);
    else setLoading(true);

    try {
      /* The template goes in its own argument, so unlike the other two
         mailer lists it can be combined with a search. An event key is
         sent as the search pair -- and the typed search, if any, is what
         the reader asked for, so it wins over the picker. */
      let value = searchTerm.trim();
      let field = value ? "eventKey" : "";
      if (!value && eventKey) {
        value = eventKey;
        field = "eventKey";
      }

      const response = await getEmailEventConfigs(
        recordsPerPage,
        currentPage,
        value,
        field,
        templateId,
      );

      const records = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response)
        ? response
        : [];
      setTotalRecords(
        Number(
          response?.pagination?.total ?? (isAppend ? totalRecords : records.length),
        ),
      );

      // Every key that has come past, so the picker can offer it.
      setEventKeysSeen((prev) => {
        const set = new Set(prev);
        records.forEach((r) => {
          if (r.eventKey) set.add(r.eventKey);
        });
        return Array.from(set).sort();
      });

      // Guarded by id so a repeated page cannot double up a row.
      if (isAppend) {
        setRows((prev) => {
          const seen = new Set(prev.map((r) => r.id));
          return [...prev, ...records.filter((r) => !seen.has(r.id))];
        });
      } else {
        setRows(records);
      }
      appendRef.current = false;
    } catch (error) {
      console.error("Error fetching email event configs:", error);
      toast.error("Error loading email event configurations!");
      if (!isAppend) {
        setRows([]);
        setTotalRecords(0);
      }
      appendRef.current = false;
    } finally {
      setLoading(false);
      setLoadingMore(false);
      isFetching.current = false;
    }
    // totalRecords is only read to hold the previous figure on an append;
    // depending on it would refetch every time it lands.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordsPerPage, currentPage, searchTerm, eventKey, templateId]);

  useEffect(() => {
    if (canView) fetchConfigs();
  }, [fetchConfigs, canView]);

  // ─── Scroll ───────────────────────────────────────────────────────────

  // The scroll asked for the next batch. Only ever moves forward, and only
  // while there is more on the server than is already on screen.
  const loadMore = useCallback(() => {
    if (isFetching.current || loading || loadingMore) return;
    if (rows.length >= totalRecords) return;
    const nextPage = Math.floor(rows.length / recordsPerPage) + 1;
    // Setting the page to what it already is changes no state and runs no
    // effect, which would strand appendRef at true and make the next fresh
    // load append to the list it was supposed to replace.
    if (nextPage === currentPage) return;
    appendRef.current = true;
    setCurrentPage(nextPage);
  }, [loading, loadingMore, rows.length, totalRecords, recordsPerPage, currentPage]);

  // The page is the scroller, so the foot of the list coming into view is
  // the signal for the next batch -- see useScrollLoadMore.
  useScrollLoadMore(listEndRef, loadMore, [loadMore, rows.length]);

  // ─── Handlers ─────────────────────────────────────────────────────────

  /* The search box does not wait for Apply. It runs itself once the
     typing stops -- the pickers beside it still wait, because
     choosing three of those should cost one refetch, not three. */
  const runSearch = useDebouncedCallback((term) => {
    appendRef.current = false;
    setSearchTerm(term.trim());
    setCurrentPage(1);
  });

  const handleApplyFilters = () => {
    /* This sets the search itself, so a keystroke still waiting out
       its delay must not land on top of it a moment later. */
    runSearch.cancel();
    appendRef.current = false;
    setSearchTerm(searchDraft.trim());
    setTemplateId(templateDraft ? templateDraft.value : "");
    setEventKey(eventKeyDraft);
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    /* This sets the search itself, so a keystroke still waiting out
       its delay must not land on top of it a moment later. */
    runSearch.cancel();
    appendRef.current = false;
    setSearchDraft("");
    setTemplateDraft(null);
    setEventKeyDraft("");
    setSearchTerm("");
    setTemplateId("");
    setEventKey("");
    setCurrentPage(1);
    setTemplateSearch("");
    setTemplatePage(1);
    setTemplateOptions([]);
    setTemplateHasMore(true);
  };

  const handleRowsPerPageChange = (newLimit) => {
    appendRef.current = false;
    setRecordsPerPage(Number(newLimit));
    setCurrentPage(1);
  };

  const hasActiveFilters =
    Boolean(searchTerm) || Boolean(templateId) || Boolean(eventKey);

  const handleDelete = async (id) => {
    const result = await confirm({
      title: "Delete Confirmation",
      tone: 'danger',
      text: "Are you sure you want to delete this Email Event Configuration?",
      confirmButtonText: "Yes, Delete it!",
      cancelButtonText: "No, Cancel",
    });

    if (!result.isConfirmed) return;

    try {
      await deleteEmailEventConfig(id);
      // Gone: the popup that showed it swaps to the done panel and closes
      // itself five seconds later.
      // Rebuild the list from page 1 so the row is gone and the count is
      // right, rather than leaving a hole in a scrolled list.
      appendRef.current = false;
      if (currentPage === 1) fetchConfigs();
      else setCurrentPage(1);
      toast.success("The configuration has been deleted successfully.");
    } catch (error) {
      console.error("Error deleting email event config:", error);
      toast.error("Failed to delete the configuration. Please try again.");
    }
  };

  // ─── Headers ──────────────────────────────────────────────────────────

  /* Five columns and no more. S.No, the template's own settings, the
     update stamp and the row of action buttons are all in the popup a row
     opens -- the same trade the attendance list makes. */
  const headers = [
    {
      title: "Event",
      prop: "eventKey",
      searchable: true,
      filterable: true,
      sortable: true,
      cell: (row) =>
        row.eventKey ? (
          <span className="pg-strong">{eventLabelOf(row.eventKey)}</span>
        ) : (
          <span className="pg-none">{DASH}</span>
        ),
    },
    {
      title: "Event Key",
      prop: "eventKeyRaw",
      filterable: true,
      cell: (row) =>
        row.eventKey ? (
          <span className="pgm-mono">{row.eventKey}</span>
        ) : (
          <span className="pg-none">{DASH}</span>
        ),
    },
    {
      title: "Template",
      prop: "emailtemplate",
      filterable: true,
      cell: (row) => {
        const name = templateNameOf(row);
        return name ? <span>{name}</span> : <span className="pg-none">{DASH}</span>;
      },
    },
    {
      title: "Created",
      prop: "createdAt",
      filterable: true,
      sortable: true,
      cell: (row) => <span className="pg-date">{dateOf(row.createdAt)}</span>,
    },
    {
      title: "Status",
      prop: "isDeleted",
      filterable: true,
      sortable: true,
      cell: (row) => (
        <span className={`pg-pill ${row.isDeleted ? "pg-pill--bad" : "pg-pill--good"}`}>
          {row.isDeleted ? "Inactive" : "Active"}
        </span>
      ),
    },
  ];

  // ─── Render ───────────────────────────────────────────────────────────

  return (
    <div className="pg">

      <SettingsFormModal
        open={Boolean(formModal)}
        eyebrow={formModal?.recordId ? "Edit configuration" : "New configuration"}
        title={formModal?.recordId ? "Edit event mail" : "New event mail"}
        subtitle="Which template goes out, and what has to happen for it to be sent."
        labelledBy="eec-form-title"
        onClose={() => setFormModal(null)}
      >
        <AddEmailEventConfig
          embedded
          recordId={formModal?.recordId}
          onCancel={() => setFormModal(null)}
          onDone={() => {
            // Saved: the dialog swaps the form for the done panel and closes
            // itself five seconds later (SettingsFormModal).
            // The record changed, so the view popup still open behind this form
            // would be showing the old one. It goes; the form shows the done panel.
            setDetail(null);
            // Rebuild from page 1 so a new configuration appears and an
            // edited one shows its new values.
            appendRef.current = false;
            if (currentPage === 1) fetchConfigs();
            else setCurrentPage(1);
          }}
        />
      </SettingsFormModal>

      <ConfigDetailModal
        detail={detail}
        canEdit={canEdit}
        canDelete={canDelete}
        onClose={() => setDetail(null)}
        onEdit={() => {
          const id = detail?.id;
          if (id) setFormModal({ recordId: String(id) });
        }}
        onDelete={() => {
          const id = detail?.id;
          if (id) handleDelete(id);
        }}
      />

      {/* Every control that narrows the list lives here, the search box
          included -- the table's own toolbar is switched off below so the
          two are not stacked one above the other. Nothing here takes
          effect until Apply, so picking three filters is one refetch. */}
      <FilterSheet label="Filter event configurations">
        {/* Row one: the search box and how many rows a load brings. The
            only searchable field is the event key, so there is no
            "Search in" to pick. */}
        <div className="pg-filters-search">
          <div className="pg-field pg-field--search">
            <label className="pg-label" htmlFor="eec-search">
              Search
            </label>
            <input
              id="eec-search"
              type="text"
              className="pg-input pg-input--search"
              placeholder="Search by event key..."
              value={searchDraft}
              onChange={(e) => {
                setSearchDraft(e.target.value);
                runSearch(e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleApplyFilters();
              }}
            />
          </div>

          <div className="pg-field pg-field--tight">
            <label className="pg-label" htmlFor="eec-rows">
              Rows per load
            </label>
            <select
              id="eec-rows"
              className="pg-select"
              value={recordsPerPage}
              onChange={(e) => handleRowsPerPageChange(e.target.value)}
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>

        {/* Row two: the pickers, then the buttons. */}
        <div className="pg-filters-row">
          <div className="pg-filters-fields pg-filters-fields--notrainer">
            <div className="pg-field">
              <label className="pg-label">Template</label>
              <NewCustomAsyncDropdown
                options={templateOptions}
                value={templateDraft ? [templateDraft] : []}
                onChange={(selected) =>
                  setTemplateDraft(
                    Array.isArray(selected) && selected.length > 0 ? selected[0] : null,
                  )
                }
                placeholder="All templates"
                isMulti={false}
                isSearchable={true}
                isLoading={templateLoading}
                searchInControl={true}
                onMenuOpen={() => {
                  if (templateOptions.length === 0 && !templateLoading) {
                    setTemplatePage(1);
                    setTemplateSearch("");
                    loadTemplates("", 1, false);
                  }
                }}
                onInputChange={handleTemplateSearch}
                onMenuScrollToBottom={() => {
                  if (templateHasMore && !templateLoading) {
                    const next = templatePage + 1;
                    setTemplatePage(next);
                    loadTemplates(templateSearch, next, true);
                  }
                }}
                onRefresh={() => {
                  setTemplateSearch("");
                  setTemplatePage(1);
                  loadTemplates("", 1, false);
                }}
              />
            </div>

            <div className="pg-field">
              <label className="pg-label" htmlFor="eec-eventkey">
                Event key
              </label>
              <select
                id="eec-eventkey"
                className="pg-select"
                value={eventKeyDraft}
                onChange={(e) => setEventKeyDraft(e.target.value)}
              >
                <option value="">All events</option>
                {eventKeysSeen.map((key) => (
                  <option key={key} value={key}>
                    {eventLabelOf(key)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="pg-filters-actions">
            <button
              type="button"
              className="pg-btn pg-btn--main"
              onClick={handleApplyFilters}
            >
              Apply
            </button>
            <button
              type="button"
              className="pg-btn"
              onClick={handleClearFilters}
              disabled={!hasActiveFilters}
            >
              Clear
            </button>

            {canAdd && (
              <>
                <span className="pg-filters-sep" aria-hidden="true" />
                <button
                  type="button"
                  className="pg-btn pg-btn--main"
                  onClick={() => setFormModal({})}
                >
                  Add configuration
                </button>
              </>
            )}
          </div>
        </div>
      </FilterSheet>

      {/* --emailevt: the rules that give a row its pointer and hover are
          unscoped, so the name has to be its own and unmistakable. */}
      <div className="td-list td-list--emailevt" ref={listRef}>
        <NewCustomDataTable
          data={rows}
          headers={headers}
          totalRecords={totalRecords}
          currentPage={currentPage}
          recordsPerPage={recordsPerPage}
          onPageChange={setCurrentPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          serverSide={true}
          loading={loading}
          loadingMore={loadingMore}
          showToolbar={false}
          showPagination={false}
          showFooter={false}
          onRowClick={(row) => setDetail(row)}
          // Every cell wraps its text in a span, so the default
          // ['TR', 'TD'] would leave the text itself unclickable and only
          // the padding around it would open the record.
          validRowClickTagNames="*"
          rowProps={(row) => ({
            tabIndex: 0,
            role: "button",
            "aria-label": `Open ${
              eventLabelOf(row.eventKey) || "this configuration"
            }`,
            onKeyDown: (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setDetail(row);
              }
            },
          })}
        />
      </div>

      {/* What the page buttons used to say, said once: how far down the
          list the reader is. Scrolling near the bottom fetches the next
          batch. */}
      <div className="pg-scroll-foot" ref={listEndRef}>
        {rows.length < totalRecords ? (
          <>
            <span className="pg-scroll-count">
              {rows.length} of {totalRecords}
            </span>
            {loadingMore && <span className="pg-spinner" aria-hidden="true" />}
          </>
        ) : (
          totalRecords > 0 && (
            <span className="pg-scroll-count">
              All {totalRecords} configuration{totalRecords === 1 ? "" : "s"} loaded
            </span>
          )
        )}
      </div>

      {!loading && rows.length === 0 && (
        <div className="pg-state">
          <span className="pg-state-icon">
            <Zap size={26} aria-hidden="true" />
          </span>
          <h3 className="pg-state-title">No event configurations found</h3>
          <p className="pg-state-text">
            {hasActiveFilters
              ? "Nothing matches the filters in force."
              : "No event has been wired to an email template yet."}
          </p>
        </div>
      )}
    </div>
  );
};

export default ListEmailEventConfig;
